
import Button from "@mui/material/Button";
import SendIcon from '@mui/icons-material/Send';
import api from "../../utils/Url";
import ToastApp from "../../ToastApp";
import { toast } from "react-toastify";
import {
  Autocomplete,
  Box,
  Card,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  useTheme,
  alpha,
  Typography,
  Divider
} from "@mui/material";
import { ConfirmDialog } from "primereact/confirmdialog";
import { DataGrid, GridColDef, GridRowSelectionModel, GridRowParams, GridToolbar } from "@mui/x-data-grid";

// Date Picker Imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import React from "react";

export default function OverdueList() {
  const theme = useTheme();
  const [applicableAcceOption, setApplicableAcceOption] = React.useState<any[]>([{ value: "-1", label: "Member Code" }]);
  const [deptData, setDeptData] = React.useState<string>("");
  const [progData, setProgData] = React.useState<string>("");
  const [timerCheck, setTimerCheck] = React.useState<NodeJS.Timeout | null>(null);
  const [applicableAcceOptionMemName, setApplicableAcceOptionMemName] = React.useState<any | null>(null);
  const [memberCode, setMemberCode] = React.useState<string>("");
  const [Category_dept_Option, setCategory_dept_Option] = React.useState<any[]>([]);
  const [programOptions, setProgramOptions] = React.useState<any[]>([]);
  const [fromDate, setFromDate] = React.useState<dayjs.Dayjs | null>(null);
  const [toDate, setToDate] = React.useState<dayjs.Dayjs | null>(null);
  const [Option, setOption] = React.useState<any[]>([]);
  const [zones, setZones] = React.useState<any[]>([]);
  const [columns, setColumns] = React.useState<GridColDef[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>([]);
  const [isSendingMail, setIsSendingMail] = React.useState(false);
  const [selectedMembers, setSelectedMembers] = React.useState<string[]>([]);
  const [isLoadingDepartments, setIsLoadingDepartments] = React.useState(false);
  const [isLoadingPrograms, setIsLoadingPrograms] = React.useState(false);

  React.useEffect(() => {
    fetchDepartments();
    fetchZonesData();
  }, []);

  React.useEffect(() => {
    return () => {
      if (timerCheck) {
        clearTimeout(timerCheck);
      }
    };
  }, [timerCheck]);

  React.useEffect(() => {
    if (deptData) {
      fetchProgramsByDepartment(deptData);
    } else {
      setProgramOptions([]);
      setProgData("");
    }
  }, [deptData]);

  const fetchDepartments = async () => {
    setIsLoadingDepartments(true);
    try {
      const response = await api.get(`Member/GetDistinctDepartmentsRaw`);
      if (response.data?.isSuccess && Array.isArray(response.data.data)) {
        const departments = response.data.data.map((dept: string) => ({
          label: dept,
          value: dept
        }));
        setCategory_dept_Option(departments);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
      toast.error("Failed to fetch departments");
    } finally {
      setIsLoadingDepartments(false);
    }
  };

  const fetchProgramsByDepartment = async (department: string) => {
    setIsLoadingPrograms(true);
    try {
      const response = await api.get(
        `Member/GetDistinctProgramByDepartment?departmentDescription=${encodeURIComponent(department)}`
      );
      if (response.data?.isSuccess && Array.isArray(response.data.data)) {
        const programs = response.data.data.map((program: string) => ({
          label: program,
          value: program
        }));
        setProgramOptions(programs);
      }
    } catch (error) {
      console.error("Error fetching programs by department:", error);
      toast.error("Failed to fetch programs for selected department");
    } finally {
      setIsLoadingPrograms(false);
    }
  };

  const getApplicableAccesion = async (nameData: string) => {
    if (!nameData || nameData.length < 1) {
      setApplicableAcceOption([{ value: "-1", label: "Type to search members" }]);
      return;
    }
    try {
      const res = await api.get(`AssigningRfid/GetMember?name=${nameData}`);
      const transformedData = transformMemberData(res.data);
      setApplicableAcceOption(
        transformedData.length > 0
          ? transformedData
          : [{ value: "-1", label: "No members found" }]
      );
    } catch (error) {
      console.error("Error fetching applicable accession:", error);
      setApplicableAcceOption([
        { value: "-1", label: "Error fetching members" },
      ]);
    }
  };

  const debouncedMemberSearch = React.useCallback(
    (query: string) => {
      if (timerCheck) {
        clearTimeout(timerCheck);
      }
      const newTimer = setTimeout(() => {
        getApplicableAccesion(query);
      }, 500);
      setTimerCheck(newTimer);
    },
    [timerCheck]
  );

  const handleMemberInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string
  ) => {
    debouncedMemberSearch(newInputValue);
  };

  const transformMemberData = (data: any[]): any[] => {
    if (!Array.isArray(data)) return [];
    return data.map((item: any) => {
      const name = item.label.replace(/^\(.*?\)/, "").trim();
      return {
        value: item.value,
        label: `${item.value} -- ${name}`,
        name: name,
      };
    });
  };

  const saveSendMail = () => {
    if (selectedMembers.length === 0) {
      toast.warn("Please select at least one member to send an email.");
      return;
    }

    setIsSendingMail(true);
    const collectData = selectedMembers;

    api
      .post(`AssigningRfid/SendMail`, collectData)
      .then((response) => {
        if (response?.data?.isSuccess === true) {
          toast.success(`Mail sent successfully to ${selectedMembers.length} member(s)!`);
          setSelectionModel([]);
          setSelectedMembers([]);
        } else {
          toast.error(response?.data?.mesg || `Mail sending failed. Please try again.`);
        }
      })
      .catch((error) => {
        console.error("Error sending mail:", error);
        toast.error("An error occurred while sending the mail.");
      })
      .finally(() => {
        setIsSendingMail(false);
      });
  };

  const fetchZonesData = async () => {
    setIsLoading(true);
    try {
      const fdate = fromDate ? fromDate.format('YYYY/MM/DD') : null;
      const tdate = toDate ? toDate.format('YYYY/MM/DD') : null;
      
      const collectData = {
        mem_cd: memberCode || "",
        mname: applicableAcceOptionMemName?.name || "",
        mem_prmntphone: "",
        mem_email: "",
        mem_prmntadd1: "",
        mem_prmntcity: "",
        dept: deptData||"",
        iss_dt: new Date().toISOString(),
        ret_dt: new Date().toISOString(),
        fine_amt: 0,
        deptcode: "",
        branch_cd:  "",
        branch_name: progData||"",
      };

      const response = await api.post(
        `AssigningRfid/Getoverduelist`,
        collectData,
        {
          params: {
            fdate: fdate,
            tdate: tdate
          }
        }
      );
      
      const data = response.data.data || [];
      const zonesWithSerial = data.map((emp: any, index: number) => ({
        ...emp,
        id: emp.slip_no || index,
        serialNo: index + 1,
      }));

      setZones(zonesWithSerial);

      if (data.length > 0) {
        const newColumns: GridColDef[] = [
          { field: "serialNo", headerName: "S.No.", width: 80 },
          { field: "mem_cd", headerName: "Member Code", flex: 1, minWidth: 120 },
          { field: "mname", headerName: "Member Name", flex: 1.5, minWidth: 180 },
          { field: "mem_prmntphone", headerName: "Phone Number", flex: 1, minWidth: 190 },
          { 
            field: "mem_email", 
            headerName: "Email", 
            flex: 1.5, 
            minWidth: 280,
            renderCell: (params) => {
              const email = params.value;
              return (email && email.trim() !== '' && email.trim() !== '.') ? email : 'NA';
            }
          },
          { field: "dept", headerName: "Department", flex: 1.5, minWidth: 240 },
          { 
            field: "iss_dt", 
            headerName: "Issue Date", 
            flex: 1, 
            minWidth: 120, 
            type: "date", 
            valueGetter: (params) => params.value ? new Date(params.value) : null,
            valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString('en-GB') : ''
          },
          { 
            field: "ret_dt", 
            headerName: "Return Date", 
            flex: 1, 
            minWidth: 120, 
            type: "date", 
            valueGetter: (params) => params.value ? new Date(params.value) : null,
            valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString('en-GB') : ''
          },
          { field: "fine_amt", headerName: "Fine Amount", flex: 0.8, minWidth: 100, type: "number" },
          { field: "branch_name", headerName: "Program", flex: 1, minWidth: 130 },
          { field: "accn_no", headerName: "Acc No", flex: 1, minWidth: 130 },
        ];
        setColumns(newColumns);
      } else {
        setColumns([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch overdue list.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Card sx={{ mb: 2, p: 2, boxShadow: 3 }}>
        <ConfirmDialog />
        <Typography variant="h5" component="div" sx={{ mb: 2 }}>
          Overdue List
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Autocomplete
                disablePortal
                id="member-code-autocomplete"
                options={applicableAcceOption}
                getOptionLabel={(option: any) => option?.label || ""}
                fullWidth
                size="small"
                value={applicableAcceOptionMemName}
                onInputChange={handleMemberInputChange}
                onChange={(e: any, newValue: any) => {
                  setApplicableAcceOptionMemName(newValue);
                  setMemberCode(newValue ? newValue.value : "");
                }}
                renderInput={(params: any) => (
                  <TextField {...params} label="Select Member Code" />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Autocomplete
                disablePortal
                id="department-autocomplete"
                options={Category_dept_Option}
                getOptionLabel={(option: any) => option?.label || ""}
                fullWidth
                size="small"
                loading={isLoadingDepartments}
                onChange={(e: any, newValue: any) => {
                  setDeptData(newValue ? newValue.value : "");
                }}
                renderInput={(params: any) => (
                  <TextField 
                    {...params} 
                    label="Select Department" 
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingDepartments ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Autocomplete
                disablePortal
                id="program-autocomplete"
                options={programOptions}
                getOptionLabel={(option: any) => option?.label || ""}
                fullWidth
                size="small"
                disabled={!deptData}
                loading={isLoadingPrograms}
                value={progData ? programOptions.find(option => option.value === progData) || null : null}
                onChange={(e: any, newValue: any) => {
                  setProgData(newValue ? newValue.value : "");
                }}
                renderInput={(params: any) => (
                  <TextField 
                    {...params} 
                    label="Select Program" 
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingPrograms ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={(newValue) => setFromDate(newValue)}
                maxDate={toDate}
                format="DD/MM/YYYY" 
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={(newValue) => setToDate(newValue)}
                minDate={fromDate}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md="auto">
              <Button
                variant="contained"
                color="primary"
                size="medium"
                fullWidth
                onClick={fetchZonesData}
                disabled={isLoading}
                sx={{ height: '40px' }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : "Search"}
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          variant="contained"
          color="secondary"
          onClick={saveSendMail}
          disabled={selectedMembers.length === 0 || isSendingMail}
          startIcon={isSendingMail ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
        >
          {isSendingMail ? "Sending..." : `Send Mail to ${selectedMembers.length} Selected Member(s)`}
        </Button>
      </Box>

      <Box sx={{ flex: 1, width: "100%" }}>
        <Paper sx={{ height: '100%', width: '100%', backgroundColor: "#FFFFFF", boxShadow: 3 }}>
          <DataGrid
            rows={zones}
            columns={columns}
            loading={isLoading}
            slots={{ toolbar: GridToolbar }}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            checkboxSelection
            onRowSelectionModelChange={(newSelectionModel) => {
              setSelectionModel(newSelectionModel);
              const selectedRowsData = zones.filter((row) => 
                newSelectionModel.includes(row.id)
              );
              const uniqueMemberCodes = Array.from(
                new Set(selectedRowsData.map((row) => row.mem_cd))
              );
              setSelectedMembers(uniqueMemberCodes);
            }}
            rowSelectionModel={selectionModel}
            getRowId={(row) => row.id}
            isRowSelectable={(params: GridRowParams) =>
              !!params.row.mem_email && 
              params.row.mem_email.trim() !== '' && 
              params.row.mem_email.trim() !== '.'
            }
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: "#2B4593",
                color: "#fff",
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: "bold",
              },
              '& .MuiDataGrid-virtualScroller': { 
                minHeight: '200px' 
              }
            }}
          />
        </Paper>
      </Box>
      <ToastApp />
    </Box>
  );
}






// import * as React from "react";
// import Button from "@mui/material/Button";
// import SendIcon from '@mui/icons-material/Send';
// import api from "../../utils/Url";
// import ToastApp from "../../ToastApp";
// import { toast } from "react-toastify";
// import {
//   Autocomplete,
//   Box,
//   Card,
//   CircularProgress,
//   Grid,
//   Paper,
//   TextField,
//   useTheme,
//   alpha,
//   Typography,
//   Divider
// } from "@mui/material";
// import { ConfirmDialog } from "primereact/confirmdialog";
// import { DataGrid, GridColDef, GridRowSelectionModel, GridRowParams, GridToolbar } from "@mui/x-data-grid";

// // Date Picker Imports
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// import dayjs from 'dayjs';

// export default function OverdueList() {
//   const theme = useTheme();
//   const [applicableAcceOption, setApplicableAcceOption] = React.useState<any[]>([{ value: "-1", label: "Member Code" }]);
//   const [deptData, setDeptData] = React.useState<string>("");
//   const [progData, setProgData] = React.useState<string>("");
//   const [timerCheck, setTimerCheck] = React.useState<NodeJS.Timeout | null>(null);
//   const [applicableAcceOptionMemName, setApplicableAcceOptionMemName] = React.useState<any | null>(null);
//   const [memberCode, setMemberCode] = React.useState<string>("");
//   const [Category_dept_Option, setCategory_dept_Option] = React.useState<any[]>([]);
//   const [fromDate, setFromDate] = React.useState<dayjs.Dayjs | null>(null);
//   const [toDate, setToDate] = React.useState<dayjs.Dayjs | null>(null);
//   const [Option, setOption] = React.useState<any[]>([]);
//   const [zones, setZones] = React.useState<any[]>([]);
//   const [columns, setColumns] = React.useState<GridColDef[]>([]);
//   const [isLoading, setIsLoading] = React.useState(false);
//   const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>([]);
//   const [isSendingMail, setIsSendingMail] = React.useState(false);
//   const [selectedMembers, setSelectedMembers] = React.useState<string[]>([]);

//   React.useEffect(() => {
//     fetchfcltydept_Data();
//     programData();
//     fetchZonesData();
//   }, []);

//   React.useEffect(() => {
//     return () => {
//       if (timerCheck) {
//         clearTimeout(timerCheck);
//       }
//     };
//   }, [timerCheck]);

//   const getApplicableAccesion = async (nameData: string) => {
//     if (!nameData || nameData.length < 1) {
//       setApplicableAcceOption([{ value: "-1", label: "Type to search members" }]);
//       return;
//     }
//     try {
//       const res = await api.get(`AssigningRfid/GetMember?name=${nameData}`);
//       const transformedData = transformMemberData(res.data);
//       setApplicableAcceOption(
//         transformedData.length > 0
//           ? transformedData
//           : [{ value: "-1", label: "No members found" }]
//       );
//     } catch (error) {
//       console.error("Error fetching applicable accession:", error);
//       setApplicableAcceOption([
//         { value: "-1", label: "Error fetching members" },
//       ]);
//     }
//   };

//   const debouncedMemberSearch = React.useCallback(
//     (query: string) => {
//       if (timerCheck) {
//         clearTimeout(timerCheck);
//       }
//       const newTimer = setTimeout(() => {
//         getApplicableAccesion(query);
//       }, 500);
//       setTimerCheck(newTimer);
//     },
//     [timerCheck]
//   );

//   const handleMemberInputChange = (
//     event: React.SyntheticEvent,
//     newInputValue: string
//   ) => {
//     debouncedMemberSearch(newInputValue);
//   };

//   const transformMemberData = (data: any[]): any[] => {
//     if (!Array.isArray(data)) return [];
//     return data.map((item: any) => {
//       const name = item.label.replace(/^\(.*?\)/, "").trim();
//       return {
//         value: item.value,
//         label: `${item.value} -- ${name}`,
//         name: name,
//       };
//     });
//   };

//   const fetchfcltydept_Data = () => {
//     const collectData = { fld_tag: "" };
//     api
//       .post(`Member/Getm_fcltydept`, collectData)
//       .then((response) => {
//         if (response.data && response.data.data) {
//           const arr = response.data.data.map((items: any) => ({
//             label: `${items.fclty_dept_dscr} (${items.fclty_dept_cd})`,
//             value: items.fclty_dept_cd,
//           }));
//           setCategory_dept_Option(arr);
//         } else {
//           setCategory_dept_Option([]);
//         }
//       })
//       .catch((error) => {
//         console.error("Error fetching faculty department data:", error);
//         setCategory_dept_Option([]);
//       });
//   };

//   const programData = () => {
//     const collectData = {
//       "branch_cd": "",
//       "branch_name": ""
//     };
//     api
//       .post(`Member/Getm_prgram`, collectData)
//       .then((response) => {
//         if (response.data && response.data.data) {
//           const arr = response.data.data.map((items: any) => ({
//             label: `${items.branch_name} (${items.branch_cd})`,
//             value: items.branch_cd,
//           }));
//           setOption(arr);
//         } else {
//           setOption([]);
//         }
//       })
//       .catch((error) => {
//         console.error("Error fetching program data:", error);
//         setOption([]);
//       });
//   };

//   const saveSendMail = () => {
//     if (selectedMembers.length === 0) {
//       toast.warn("Please select at least one member to send an email.");
//       return;
//     }

//     setIsSendingMail(true);
//     const collectData = selectedMembers;

//     api
//       .post(`AssigningRfid/SendMail`, collectData)
//       .then((response) => {
//         if (response?.data?.isSuccess === true) {
//           toast.success(`Mail sent successfully to ${selectedMembers.length} member(s)!`);
//           setSelectionModel([]);
//           setSelectedMembers([]);
//         } else {
//           toast.error(response?.data?.mesg || `Mail sending failed. Please try again.`);
//         }
//       })
//       .catch((error) => {
//         console.error("Error sending mail:", error);
//         toast.error("An error occurred while sending the mail.");
//       })
//       .finally(() => {
//         setIsSendingMail(false);
//       });
//   };

//   const fetchZonesData = async () => {
//     setIsLoading(true);
//     try {
//       const fdate = fromDate ? fromDate.format('YYYY/MM/DD') : null;
//       const tdate = toDate ? toDate.format('YYYY/MM/DD') : null;
      
//       const collectData = {
//         mem_cd: memberCode || "",
//         mname: applicableAcceOptionMemName?.name || "",
//         mem_prmntphone: "",
//         mem_email: "",
//         mem_prmntadd1: "",
//         mem_prmntcity: "",
//         dept: "",
//         iss_dt: new Date().toISOString(),
//         ret_dt: new Date().toISOString(),
//         fine_amt: 0,
//         deptcode: deptData || "",
//         branch_cd: progData || "",
//         branch_name: "",
//       };

//       const response = await api.post(
//         `AssigningRfid/Getoverduelist`,
//         collectData,
//         {
//           params: {
//             fdate: fdate,
//             tdate: tdate
//           }
//         }
//       );
      
//       const data = response.data.data || [];
//       const zonesWithSerial = data.map((emp: any, index: number) => ({
//         ...emp,
//         id: emp.slip_no || index,
//         serialNo: index + 1,
//       }));

//       setZones(zonesWithSerial);

//       if (data.length > 0) {
//         const newColumns: GridColDef[] = [
//           { field: "serialNo", headerName: "S.No.", width: 80 },
//           { field: "mem_cd", headerName: "Member Code", flex: 1, minWidth: 120 },
//           { field: "mname", headerName: "Member Name", flex: 1.5, minWidth: 180 },
//           { field: "mem_prmntphone", headerName: "Phone Number", flex: 1, minWidth: 190 },
//           { 
//             field: "mem_email", 
//             headerName: "Email", 
//             flex: 1.5, 
//             minWidth: 280,
//             renderCell: (params) => {
//               const email = params.value;
//               return (email && email.trim() !== '' && email.trim() !== '.') ? email : 'NA';
//             }
//           },
//           { field: "dept", headerName: "Department", flex: 1.5, minWidth: 240 },
//           { 
//             field: "iss_dt", 
//             headerName: "Issue Date", 
//             flex: 1, 
//             minWidth: 120, 
//             type: "date", 
//             valueGetter: (params) => params.value ? new Date(params.value) : null,
//             valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString('en-GB') : ''
//           },
//           { 
//             field: "ret_dt", 
//             headerName: "Return Date", 
//             flex: 1, 
//             minWidth: 120, 
//             type: "date", 
//             valueGetter: (params) => params.value ? new Date(params.value) : null,
//             valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString('en-GB') : ''
//           },
//           { field: "fine_amt", headerName: "Fine Amount", flex: 0.8, minWidth: 100, type: "number" },
//           { field: "branch_name", headerName: "Program", flex: 1, minWidth: 130 },
//           { field: "accn_no", headerName: "Acc No", flex: 1, minWidth: 130 },
//         ];
//         setColumns(newColumns);
//       } else {
//         setColumns([]);
//       }
//     } catch (error) {
//       console.error("Error fetching data:", error);
//       toast.error("Failed to fetch overdue list.");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   return (
//     <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
//       <Card sx={{ mb: 2, p: 2, boxShadow: 3 }}>
//         <ConfirmDialog />
//         <Typography variant="h5" component="div" sx={{ mb: 2 }}>
//           Overdue List
//         </Typography>
//         <Divider sx={{ mb: 2 }} />

//         <LocalizationProvider dateAdapter={AdapterDayjs}>
//           <Grid container spacing={2} alignItems="center">
//             <Grid item xs={12} sm={6} md={4} lg={3}>
//               <Autocomplete
//                 disablePortal
//                 id="member-code-autocomplete"
//                 options={applicableAcceOption}
//                 getOptionLabel={(option: any) => option?.label || ""}
//                 fullWidth
//                 size="small"
//                 value={applicableAcceOptionMemName}
//                 onInputChange={handleMemberInputChange}
//                 onChange={(e: any, newValue: any) => {
//                   setApplicableAcceOptionMemName(newValue);
//                   setMemberCode(newValue ? newValue.value : "");
//                 }}
//                 renderInput={(params: any) => (
//                   <TextField {...params} label="Select Member Code" />
//                 )}
//               />
//             </Grid>

//             <Grid item xs={12} sm={6} md={4} lg={3}>
//               <Autocomplete
//                 disablePortal
//                 id="department-autocomplete"
//                 options={Category_dept_Option}
//                 getOptionLabel={(option: any) => option?.label || ""}
//                 fullWidth
//                 size="small"
//                 onChange={(e: any, newValue: any) => {
//                   setDeptData(newValue ? newValue.value : "");
//                 }}
//                 renderInput={(params: any) => (
//                   <TextField {...params} label="Select Department" />
//                 )}
//               />
//             </Grid>

//             <Grid item xs={12} sm={6} md={4} lg={3}>
//               <Autocomplete
//                 disablePortal
//                 id="program-autocomplete"
//                 options={Option}
//                 getOptionLabel={(option: any) => option?.label || ""}
//                 fullWidth
//                 size="small"
//                 onChange={(e: any, newValue: any) => {
//                   setProgData(newValue ? newValue.value : "");
//                 }}
//                 renderInput={(params: any) => (
//                   <TextField {...params} label="Select Program" />
//                 )}
//               />
//             </Grid>
            
//             <Grid item xs={12} sm={6} md={4} lg={3}>
//               <DatePicker
//                 label="From Date"
//                 value={fromDate}
//                 onChange={(newValue) => setFromDate(newValue)}
//                 maxDate={toDate}
//                 format="DD/MM/YYYY" 
//                 slotProps={{ textField: { size: 'small', fullWidth: true } }}
//               />
//             </Grid>
            
//             <Grid item xs={12} sm={6} md={4} lg={3}>
//               <DatePicker
//                 label="To Date"
//                 value={toDate}
//                 onChange={(newValue) => setToDate(newValue)}
//                 minDate={fromDate}
//                 format="DD/MM/YYYY"
//                 slotProps={{ textField: { size: 'small', fullWidth: true } }}
//               />
//             </Grid>

//             <Grid item xs={12} sm={6} md="auto">
//               <Button
//                 variant="contained"
//                 color="primary"
//                 size="medium"
//                 fullWidth
//                 onClick={fetchZonesData}
//                 disabled={isLoading}
//                 sx={{ height: '40px' }}
//               >
//                 {isLoading ? <CircularProgress size={24} color="inherit" /> : "Search"}
//               </Button>
//             </Grid>
//           </Grid>
//         </LocalizationProvider>
//       </Card>

//       <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
//         <Button
//           variant="contained"
//           color="secondary"
//           onClick={saveSendMail}
//           disabled={selectedMembers.length === 0 || isSendingMail}
//           startIcon={isSendingMail ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
//         >
//           {isSendingMail ? "Sending..." : `Send Mail to ${selectedMembers.length} Selected Member(s)`}
//         </Button>
//       </Box>

//       <Box sx={{ flex: 1, width: "100%" }}>
//         <Paper sx={{ height: '100%', width: '100%', backgroundColor: "#FFFFFF", boxShadow: 3 }}>
//           <DataGrid
//             rows={zones}
//             columns={columns}
//             loading={isLoading}
//             slots={{ toolbar: GridToolbar }}
//             pageSizeOptions={[5, 10, 25, 50, 100]}
//             initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
//             slotProps={{
//               toolbar: {
//                 showQuickFilter: true,
//                 quickFilterProps: { debounceMs: 500 },
//               },
//             }}
//             checkboxSelection
//             onRowSelectionModelChange={(newSelectionModel) => {
//               setSelectionModel(newSelectionModel);
//               const selectedRowsData = zones.filter((row) => 
//                 newSelectionModel.includes(row.id)
//               );
//               const uniqueMemberCodes = Array.from(
//                 new Set(selectedRowsData.map((row) => row.mem_cd))
//               );
//               setSelectedMembers(uniqueMemberCodes);
//             }}
//             rowSelectionModel={selectionModel}
//             getRowId={(row) => row.id}
//             isRowSelectable={(params: GridRowParams) =>
//               !!params.row.mem_email && 
//               params.row.mem_email.trim() !== '' && 
//               params.row.mem_email.trim() !== '.'
//             }
//             sx={{
//               '& .MuiDataGrid-columnHeaders': {
//                 backgroundColor: "#2B4593",
//                 color: "#fff",
//               },
//               '& .MuiDataGrid-columnHeaderTitle': {
//                 fontWeight: "bold",
//               },
//               '& .MuiDataGrid-virtualScroller': { 
//                 minHeight: '200px' 
//               }
//             }}
//           />
//         </Paper>
//       </Box>
//       <ToastApp />
//     </Box>
//   );
// }


















// import * as React from "react";
// import Button from "@mui/material/Button";
// import Dialog from "@mui/material/Dialog";
// import AppBar from "@mui/material/AppBar";
// import Toolbar from "@mui/material/Toolbar";
// import IconButton from "@mui/material/IconButton";
// import Typography from "@mui/material/Typography";
// import CloseIcon from "@mui/icons-material/Close";
// import SendIcon from '@mui/icons-material/Send';
// import api from "../../utils/Url"; // Assuming this path is correct

// import ToastApp from "../../ToastApp"; // Assuming this path is correct
// import { toast } from "react-toastify";
// import {
//   Autocomplete,
//   Box,
//   Card,
//   CircularProgress,
//   Grid,
//   Paper,
//   TextField,
//   useTheme,
//   alpha,
// } from "@mui/material";
// import { ConfirmDialog } from "primereact/confirmdialog"; // Assuming PrimeReact is configured
// import { DataGrid, GridColDef, GridRowSelectionModel, GridRowParams, GridToolbar } from "@mui/x-data-grid";

// // --- New Imports for Date Picker ---
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// import dayjs from 'dayjs';

// export interface OverdueListDialogProps {
//   open: boolean;
//   handleClose: () => void;
// }

// export default function OverdueList({
//   open,
//   handleClose,
// }: OverdueListDialogProps) {
//   const theme = useTheme();
//   const [applicableAcceOption, setApplicableAcceOptionState] = React.useState<
//     any[]
//   >([{ value: "-1", label: "Member Code" }]);
//   const [deptData, setDeptData] = React.useState<string>("");
//   const [progData, setProgData] = React.useState<string>("");
//   const [timerCheck, setTimerCheck] =
//     React.useState<NodeJS.Timeout | null>(null);
//   const [
//     applicableAcceOptionMemName,
//     setApplicableAcceOptionMemName,
//   ] = React.useState<any | null>(null);
//   const [memberCode, setMemberCode] = React.useState<string>("");
//   const [Category_dept_Option, setCategory_dept_Option] = React.useState<
//     any[]
//   >([]);

//   // --- New State for Date Filters ---
//   const [fromDate, setFromDate] = React.useState<dayjs.Dayjs | null>(null);
//   const [toDate, setToDate] = React.useState<dayjs.Dayjs | null>(null);


//   const [Option, setOption] = React.useState<
//     any[]
//   >([]);
//   const [zones, setZones] = React.useState<any[]>([]);
//   const [columns, setColumns] = React.useState<GridColDef[]>([]);
//   const [isLoading, setIsLoading] = React.useState(false);

//   const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>([]);
//   const [isSendingMail, setIsSendingMail] = React.useState(false);
//   // --- State to hold unique selected members for the mail action ---
//   const [selectedMembers, setSelectedMembers] = React.useState<string[]>([]);


//   React.useEffect(() => {
//     fetchfcltydept_Data();
//     fetchZonesData();
//     programData();
//   }, []);

//   React.useEffect(() => {
//     return () => {
//       if (timerCheck) {
//         clearTimeout(timerCheck);
//       }
//     };
//   }, [timerCheck]);

//   const getApplicableAccesion = async (nameData: string) => {
//     if (!nameData || nameData.length < 1) {
//       setApplicableAcceOptionState([{ value: "-1", label: "Type to search members" }]);
//       return;
//     }
//     try {
//       const res = await api.get(`AssigningRfid/GetMember?name=${nameData}`);
//       const transformedData = transformMemberData(res.data);
//       setApplicableAcceOptionState(
//         transformedData.length > 0
//           ? transformedData
//           : [{ value: "-1", label: "No members found" }]
//       );
//     } catch (error) {
//       console.error("Error fetching applicable accession:", error);
//       setApplicableAcceOptionState([
//         { value: "-1", label: "Error fetching members" },
//       ]);
//     }
//   };

//   const debouncedMemberSearch = React.useCallback(
//     (query: string) => {
//       if (timerCheck) {
//         clearTimeout(timerCheck);
//       }
//       const newTimer = setTimeout(() => {
//         getApplicableAccesion(query);
//       }, 500);
//       setTimerCheck(newTimer);
//     },
//     [timerCheck]
//   );

//   const handleMemberInputChange = (
//     event: React.SyntheticEvent,
//     newInputValue: string
//   ) => {
//     debouncedMemberSearch(newInputValue);
//   };

//   const transformMemberData = (data: any[]): any[] => {
//     if (!Array.isArray(data)) return [];
//     return data.map((item: any) => {
//       const name = item.label.replace(/^\(.*?\)/, "").trim();
//       return {
//         value: item.value,
//         label: `${item.value} -- ${name}`,
//         name: name,
//       };
//     });
//   };

//   const fetchfcltydept_Data = () => {
//     const collectData = { fld_tag: "" };
//     api
//       .post(`Member/Getm_fcltydept`, collectData)
//       .then((response) => {
//         if (response.data && response.data.data) {
//           const arr = response.data.data.map((items: any) => ({
//             label: `${items.fclty_dept_dscr} (${items.fclty_dept_cd})`,
//             value: items.fclty_dept_cd,
//           }));
//           setCategory_dept_Option(arr);
//         } else {
//           setCategory_dept_Option([]);
//         }
//       })
//       .catch((error) => {
//         console.error("Error fetching faculty department data:", error);
//         setCategory_dept_Option([]);
//       });
//   };


//   const programData = () => {
//     const collectData = {
//       "branch_cd": "",
//       "branch_name": ""
//     };
//     api
//       .post(`Member/Getm_prgram`, collectData)
//       .then((response) => {
//         if (response.data && response.data.data) {
//           const arr = response.data.data.map((items: any) => ({
//             label: `${items.branch_name} (${items.branch_cd})`,
//             value: items.branch_cd,
//           }));
//           setOption(arr);
//         } else {
//           setOption([]);
//         }
//       })
//       .catch((error) => {
//         console.error("Error fetching program data:", error);
//         setOption([]);
//       });
//   };

//   const saveSendMail = () => {
//     if (selectedMembers.length === 0) {
//       toast.warn("Please select at least one member to send an email.");
//       return;
//     }

//     setIsSendingMail(true);
//     // --- Send the array of unique member codes ---
//     const collectData = selectedMembers;

//     api
//       .post(`AssigningRfid/SendMail`, collectData)
//       .then((response) => {
//         if (response?.data?.isSuccess === true) {
//           toast.success(`Mail sent successfully to ${selectedMembers.length} member(s)!`);
//           setSelectionModel([]); // Clear selection in the grid
//           setSelectedMembers([]); // Clear the derived member list
//         } else {
//           toast.error(response?.data?.mesg || `Mail sending failed. Please try again.`);
//         }
//       })
//       .catch((error) => {
//         console.error("Error sending mail:", error);
//         toast.error("An error occurred while sending the mail.");
//       })
//       .finally(() => {
//         setIsSendingMail(false);
//       });
//   };

//   const fetchZonesData = async () => {
//       setIsLoading(true);
//       try {
//          const fdate = fromDate ? fromDate.format('YYYY/MM/DD') :null;
//       const tdate = toDate ? toDate.format('YYYY/MM/DD') : null;
      
//       const collectData = {
//         mem_cd: memberCode || "",
//         mname: applicableAcceOptionMemName?.name || "",
//         mem_prmntphone: "",
//         mem_email: "",
//         mem_prmntadd1: "",
//         mem_prmntcity: "",
//         dept: "",
//         iss_dt: new Date().toISOString(),
//         ret_dt: new Date().toISOString(),
//         fine_amt: 0,
//         deptcode: deptData || "",
//         branch_cd: progData || "",
//         branch_name: "",
//       };

//       const response = await api.post(
//         `AssigningRfid/Getoverduelist`,
//         collectData,
//        {
//           params: {
//             fdate: fdate,
//             tdate: tdate
//           }
//         }
//       );
        
//         const data = response.data.data || [];
//         const zonesWithSerial = data.map((emp: any) => ({
//           ...emp,
//           serialNo: emp.srno,
//         }));
  
//         setZones(zonesWithSerial);
  
//         // --- Set columns only once to avoid re-renders ---
//         if (data.length > 0 && columns.length === 0) {
//           const newColumns: GridColDef[] = [
//             { field: "serialNo", headerName: "S.No.", width: 80 },
//             { field: "mem_cd", headerName: "Member Code", flex: 1, minWidth: 120 },
//             { field: "mname", headerName: "Member Name", flex: 1.5, minWidth: 180 },
//             { field: "mem_prmntphone", headerName: "Phone No.", flex: 1, minWidth: 150 },
//             { field: "mem_email", headerName: "Email", flex: 1.5, minWidth: 200 },
//             { field: "dept", headerName: "Department", flex: 1.5, minWidth: 180 },
//             { 
//               field: "iss_dt", 
//               headerName: "Issue Date", 
//               flex: 1, 
//               minWidth: 120, 
//               type: "date", 
//               valueGetter: (params) => params.value ? new Date(params.value) : null,
//               valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString('en-GB') : '' // Format: DD/MM/YYYY
//             },
//             { 
//               field: "ret_dt", 
//               headerName: "Return Date", 
//               flex: 1, 
//               minWidth: 120, 
//               type: "date", 
//               valueGetter: (params) => params.value ? new Date(params.value) : null,
//               valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString('en-GB') : '' // Format: DD/MM/YYYY
//             },
//             { field: "fine_amt", headerName: "Fine Amount", flex: 0.8, minWidth: 100, type: "number" },
//             { field: "branch_name", headerName: "Branch Name", flex: 1, minWidth: 130 },
//             { field: "accn_no", headerName: "Acc No", flex: 1, minWidth: 130 },
//           ];
//           setColumns(newColumns as any);
//         } else if (data.length === 0) {
//             setColumns([]); // Clear columns if no data
//         }
  
//       } catch (error) {
//         console.error("Error fetching data:", error);
//         toast.error("Failed to fetch overdue list.");
//       } finally {
//         setIsLoading(false);
//       }
//     };
  
//     const adjustedColumns = columns.map((column: any) => ({
//       ...column,
//     }));
  

//   return (
//     <Dialog fullScreen open={open} onClose={handleClose} PaperProps={{ sx: { backgroundColor: theme.palette.background.default, width: "90%", height: "90%" } }}>
//       <AppBar sx={{ position: "relative" }}>
//         <Toolbar>
//           <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
//             <CloseIcon />
//           </IconButton>
//           <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
//             Overdue List
//           </Typography>
//         </Toolbar>
//       </AppBar>
//       <LocalizationProvider dateAdapter={AdapterDayjs}>
//         <Box sx={{ p: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', height: 'calc(100% - 64px)' }}>
//           <Card sx={{ mb: 2, p: 2, boxShadow: 3 }}>
//             <ConfirmDialog />
//             <Grid container spacing={2} alignItems="center">
//               <Grid item xs={12} sm={6} md={4} lg={3}>
//                 <Autocomplete
//                   disablePortal
//                   id="member-code-autocomplete-dialog"
//                   options={applicableAcceOption}
//                   getOptionLabel={(option: any) => option?.label || ""}
//                   fullWidth
//                   size="small"
//                   value={applicableAcceOptionMemName}
//                   onInputChange={handleMemberInputChange}
//                   onChange={(e: any, newValue: any) => {
//                     setApplicableAcceOptionMemName(newValue);
//                     setMemberCode(newValue ? newValue.value : "");
//                   }}
//                   renderInput={(params: any) => (
//                     <TextField {...params} label="Select Member Code" />
//                   )}
//                 />
//               </Grid>

//               <Grid item xs={12} sm={6} md={4} lg={3}>
//                 <Autocomplete
//                   disablePortal
//                   id="department-autocomplete-dialog"
//                   options={Category_dept_Option}
//                   getOptionLabel={(option: any) => option?.label || ""}
//                   fullWidth
//                   size="small"
//                   onChange={(e: any, newValue: any) => {
//                     setDeptData(newValue ? newValue.value : "");
//                   }}
//                   renderInput={(params: any) => (
//                     <TextField {...params} label="Select Department" />
//                   )}
//                 />
//               </Grid>

//               <Grid item xs={12} sm={6} md={4} lg={3}>
//                 <Autocomplete
//                   disablePortal
//                   id="program-autocomplete-dialog"
//                   options={Option}
//                   getOptionLabel={(option: any) => option?.label || ""}
//                   fullWidth
//                   size="small"
//                   onChange={(e: any, newValue: any) => {
//                     setProgData(newValue ? newValue.value : "");
//                   }}
//                   renderInput={(params: any) => (
//                     <TextField {...params} label="Select Program" />
//                   )}
//                 />
//               </Grid>
              
//               {/* --- CHANGE HERE: Added format prop --- */}
//               <Grid item xs={12} sm={6} md={4} lg={3}>
//                 <DatePicker
//                   label="From Date"
//                   value={fromDate}
//                   onChange={(newValue) => setFromDate(newValue)}
//                   maxDate={toDate}
//                   format="DD/MM/YYYY" 
//                   slotProps={{ textField: { size: 'small', fullWidth: true } }}
//                 />
//               </Grid>
//               {/* --- CHANGE HERE: Added format prop --- */}
//               <Grid item xs={12} sm={6} md={4} lg={3}>
//                 <DatePicker
//                   label="To Date"
//                   value={toDate}
//                   onChange={(newValue) => setToDate(newValue)}
//                   minDate={fromDate}
//                   format="DD/MM/YYYY"
//                   slotProps={{ textField: { size: 'small', fullWidth: true } }}
//                 />
//               </Grid>

//               <Grid item xs={12} sm={6} md="auto" >
//                 <Button
//                   variant="contained"
//                   color="primary"
//                   size="medium"
//                   fullWidth
//                   onClick={() => fetchZonesData()}
//                   disabled={isLoading}
//                   sx={{ height: '40px' }}
//                 >
//                   {isLoading ? <CircularProgress size={24} color="inherit" /> : "Search"}
//                 </Button>
//               </Grid>
//             </Grid>
//           </Card>

//           <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
//             <Button
//               variant="contained"
//               color="secondary"
//               onClick={saveSendMail}
//               disabled={selectedMembers.length === 0 || isSendingMail}
//               startIcon={isSendingMail ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
//             >
//               {isSendingMail ? "Sending..." : `Send Mail to ${selectedMembers.length} Selected Member(s)`}
//             </Button>
//           </Box>

//           <Box sx={{ flexGrow: 1, width: "100%", overflow: 'hidden' }}>
//             <Paper sx={{ height: '100%', width: '100%', backgroundColor: "#FFFFFF", boxShadow: 3, overflow: 'hidden' }}>
//               <DataGrid
//                 rows={zones}
//                 columns={adjustedColumns}
//                 loading={isLoading}
//                 slots={{ toolbar: GridToolbar }}
//                 pageSizeOptions={[5, 10, 25, 50, 100]}
//                 initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
//                 slotProps={{
//                   toolbar: {
//                     showQuickFilter: true,
//                     quickFilterProps: { debounceMs: 500 },
//                   },
//                 }}
//                 checkboxSelection
//                 onRowSelectionModelChange={(newSelectionModel) => {
//                   setSelectionModel(newSelectionModel);
//                   // --- Derive unique member codes from the selected rows ---
//                   const selectedRowsData:any = zones.filter((row:any) => newSelectionModel.includes(row.slip_no.trim()));
//                  const uniqueMemberCodes:any = Array.from(new Set(selectedRowsData.map((row: any) => row.mem_cd)));
//                   setSelectedMembers(uniqueMemberCodes);
//                 }}
//                 rowSelectionModel={selectionModel}
//                 // --- FIX: Use a guaranteed unique ID for each row. slip_no is unique per overdue item. ---
//                 getRowId={(row) => row.slip_no.trim()}
//                 isRowSelectable={(params: GridRowParams) =>
//                   // --- FIX: Also check for placeholder emails like '.' ---
//                   !!params.row.mem_email && params.row.mem_email.trim() !== '' && params.row.mem_email.trim() !== '.'
//                 }
//                 sx={{
//                   border: 0,
//                   '& .MuiDataGrid-columnHeadersInner': { backgroundColor: "#2B4593" },
//                   '& .MuiDataGrid-columnHeader': {
//                     color: "#fff",
//                     fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
//                     fontWeight: "bold",
//                     '&:focus, &:focus-within': { outline: 'none' }
//                   },
//                   '& .MuiDataGrid-columnHeaderTitle': {
//                     fontWeight: "bold",
//                     overflow: 'visible',
//                     whiteSpace: 'normal',
//                     lineHeight: 'normal',
//                   },
//                   '& .MuiDataGrid-columnHeader .MuiSvgIcon-root, & .MuiDataGrid-menuIcon .MuiSvgIcon-root': { color: "#fff" },
//                   '& .MuiDataGrid-toolbarContainer': {
//                     padding: theme.spacing(1, 1.5),
//                     borderBottom: `1px solid ${theme.palette.divider}`,
//                     backgroundColor: alpha(theme.palette.background.paper, 0.95),
//                   },
//                   '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: alpha(theme.palette.action.hover, 0.03) },
//                   '& .MuiDataGrid-row:hover': { backgroundColor: alpha(theme.palette.primary.light, 0.1) },
//                   '& .MuiDataGrid-cell': {
//                     fontSize: { xs: "0.75rem", sm: "0.875rem" },
//                     padding: '0 8px',
//                     borderBottom: `1px solid ${theme.palette.divider}`,
//                   },
//                   '& .MuiTextField-root[class*="MuiDataGrid-toolbarQuickFilter"]': { marginRight: theme.spacing(1) }
//                 }}
//               />
//             </Paper>
//           </Box>
//           <ToastApp />
//         </Box>
//       </LocalizationProvider>
//     </Dialog>
//   );
// }